import React, { useState, useEffect } from 'react';
import { ScenarioDefinitionType } from '../../api/scenarioDefinitions';
import styles from './ScenarioScriptEditor.module.css';
import messagesStyles from './EditScenarioMessages.module.css';
import { useDevice } from '../../context/DeviceContext';
import Editor from 'react-simple-code-editor';
import { NARRATOR_TOKEN, USER_SLOT_TOKEN, CHARACTER_SLOT_TOKEN } from '../../utils/hjs';

interface ScenarioScriptEditorProps {
  definition: ScenarioDefinitionType;
  setDefinition: React.Dispatch<React.SetStateAction<ScenarioDefinitionType>>;
  onClose: () => void;
}

// Helper function to extract error messages from Django REST Framework response
const extractErrorMessage = (err: any): string => {
  if (!err.response || !err.response.data) {
    return 'Failed to save script. Please check for syntax errors.';
  }
  
  const { data } = err.response;
  
  // Handle string error
  if (typeof data === 'string') return data;
  
  // Handle common DRF error fields
  if (data.detail) return data.detail;
  if (data.script) return Array.isArray(data.script) ? data.script.join('\n') : data.script;
  if (data.non_field_errors) return Array.isArray(data.non_field_errors) ? data.non_field_errors.join('\n') : data.non_field_errors;
  
  // Format the entire error object if no specific fields found
  return Object.entries(data)
    .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`)
    .join('\n');
};

// Helper function to highlight character names
const highlightCharacterNames = (code: string, characterNames: string[]): string => {
  if (!characterNames.length) return code;
  
  // Create a map to quickly look up if a name needs highlighting
  const characterMap = new Set(characterNames);
  
  // Simple line-by-line approach
  return code.split('\n').map(line => {
    // Check if line starts with a character name followed by ": "
    const colonIndex = line.indexOf(': ');
    if (colonIndex > 0) {
      const possibleName = line.substring(0, colonIndex);
      if (characterMap.has(possibleName)) {
        // Replace just the name part with the highlighted version
        return `<span class="${styles.highlightCharacter}">${possibleName}</span>${line.substring(colonIndex)}`;
      }
    }
    return line;
  }).join('\n');
};

// Custom highlighter function
const highlightWithTokens = (code: string, characterNames: string[] = []) => {
  // Only apply highlighting if there's content
  if (!code) return '';

  // Replace tokens with highlighted versions
  let highlighted = code
    .replace(
      new RegExp(NARRATOR_TOKEN, 'g'), 
      `<span class="${styles.highlightNarrator}">${NARRATOR_TOKEN}</span>`
    )
    .replace(
      new RegExp(USER_SLOT_TOKEN, 'g'), 
      `<span class="${styles.highlightUserSlot}">${USER_SLOT_TOKEN}</span>`
    )
    .replace(
      new RegExp(CHARACTER_SLOT_TOKEN, 'g'), 
      `<span class="${styles.highlightCharacterSlot}">${CHARACTER_SLOT_TOKEN}</span>`
    );
    
  // Apply character name highlighting
  highlighted = highlightCharacterNames(highlighted, characterNames);

  return highlighted;
};

function ScenarioScriptEditor({ definition, setDefinition, onClose }: ScenarioScriptEditorProps): JSX.Element {
  const [script, setScript] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const { isMobile } = useDevice();

  // Extract character names for highlighting
  const characterNames = definition.characters ? 
    definition.characters.map(char => char.display_name) : [];

  // Fetch the script when component mounts
  useEffect(() => {
    const fetchScript = async () => {
      try {
        setIsLoading(true);
        const scriptContent = await definition.getScript();
        setScript(scriptContent);
        setError(null);
      } catch (err) {
        console.error('Failed to fetch script:', err);
        setError('Failed to load script. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchScript();
  }, [definition]);

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setError(null);
      const updatedDefinition = await definition.updateFromScript(script);
      setDefinition(updatedDefinition);
      // Show success feedback briefly before closing
      setTimeout(() => {
        onClose();
      }, 500);
    } catch (err: any) {
      console.error('Failed to save script:', err);
      setError(extractErrorMessage(err));
    } finally {
      setIsSaving(false);
    }
  };

  // Create editor content that can be used in both mobile and desktop layouts
  const editorContent = (
    <>
      <div className={`${messagesStyles.sectionHeader} ${isMobile ? messagesStyles.mobileHeader : ''}`}>
        <h3 className={messagesStyles.sectionTitle}>Messages</h3>
        <div className={messagesStyles.headerButtons}>
          <button 
            className={`${messagesStyles.iconButton} ${messagesStyles.cancelButton}`}
            onClick={onClose}
            disabled={isSaving}
          >
            Cancel
          </button>
          <button 
            className={`${messagesStyles.iconButton} ${messagesStyles.saveButton}`}
            onClick={handleSave}
            disabled={isLoading || isSaving}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
      
      {error && (
        <div className={styles.errorMessage}>
          {error.split('\n').map((line, i) => (
            <React.Fragment key={i}>
              {line}
              {i < error.split('\n').length - 1 && <br />}
            </React.Fragment>
          ))}
        </div>
      )}
      
      {isLoading ? (
        <div className={styles.loadingContainer}>
          <div className={styles.spinner}></div>
          <p>Loading script...</p>
        </div>
      ) : (
        <Editor
          value={script}
          onValueChange={setScript}
          highlight={code => highlightWithTokens(code, characterNames)}
          padding={10}
          className={styles.scriptEditor}
          disabled={isSaving}
          textareaClassName={styles.editorTextarea}
          preClassName={styles.editorPre}
          placeholder="Enter scenario script here..."
        />
      )}
    </>
  );

  // For mobile, return a full-screen overlay
  if (isMobile) {
    return (
      <div className={styles.scriptEditorContainer}>
        {editorContent}
      </div>
    );
  }

  // For desktop, return a container that doesn't cover the sidebar
  return (
    <div className={styles.scriptEditorDesktopContainer}>
      {editorContent}
    </div>
  );
}

export default ScenarioScriptEditor; 